import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { FiMapPin, FiPhone, FiMail, FiClock, FiNavigation } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import PageBanner from "../../components/layout/PageBanner";
import { validateEnquiryForm } from "../../utils/validators";
import "./Contact.css";

const initialForm = {
  fullName: "", phone: "", email: "", goal: "", program: "",
  contactTime: "", message: "", consent: false,
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => {
    const value = field === "consent" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validation = validateEnquiryForm(form);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      setSubmitted(false);
      return;
    }
    setErrors({});
    setSubmitted(true);
    // TODO: wire to backend enquiry endpoint once available. Form data
    // in `form` is already structured and ready to POST as JSON.
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Firm Fit Factory Peelamedu, Coimbatore</title>
        <meta name="description" content="Get in touch with Firm Fit Factory in Peelamedu, Coimbatore. Call, WhatsApp, or send an enquiry to start your fitness journey." />
        <link rel="canonical" href="https://www.firmfitfactory.com/contact" />
      </Helmet>

      <PageBanner eyebrow="Get In Touch" title="Contact Us" description="Questions about programs, membership or a trial session? Reach out — we usually reply within one business day." />

      <section className="section contact-page">
        <div className="container contact-page__grid">
          <div className="contact-info">
            <h2>Visit The Factory</h2>
            <div className="contact-info__item">
              <FiMapPin aria-hidden="true" />
              <div>
                <h3>Address</h3>
                <p>Maheshwari Nagar, Vilangurichi Road, 4th Street, Peelamedu, Coimbatore – 641004, Tamil Nadu</p>
              </div>
            </div>
            <div className="contact-info__item">
              <FiPhone aria-hidden="true" />
              <div>
                <h3>Phone</h3>
                <a href="tel:+919500886368">+91 95008 86368</a>
              </div>
            </div>
            <div className="contact-info__item">
              <FiMail aria-hidden="true" />
              <div>
                <h3>Email</h3>
                <p>info@firmfitfactory.com <em>(placeholder)</em></p>
              </div>
            </div>
            <div className="contact-info__item">
              <FiClock aria-hidden="true" />
              <div>
                <h3>Business Hours</h3>
                <p>6:00 AM – 9:00 PM (placeholder — confirm before publishing)</p>
              </div>
            </div>

            <div className="contact-info__actions">
              <a href="tel:+919500886368" className="btn btn-primary"><FiPhone className="btn-icon" aria-hidden="true" /> Click to Call</a>
              <a href="https://wa.me/919500886368" className="btn btn-outline" target="_blank" rel="noopener noreferrer">
                <FaWhatsapp className="btn-icon" aria-hidden="true" /> WhatsApp Us
              </a>
            </div>

            <div className="contact-info__map">
              <iframe
                title="Firm Fit Factory location map"
                src="https://www.google.com/maps?q=Maheshwari+Nagar+Vilangurichi+Road+Peelamedu+Coimbatore+641004&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Maheshwari+Nagar+Vilangurichi+Road+Peelamedu+Coimbatore+641004"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost contact-info__directions"
              >
                <FiNavigation aria-hidden="true" /> Get Directions
              </a>
            </div>

            <p className="contact-info__nearby">
              Located off Vilangurichi Road in Peelamedu — a short drive from Peelamedu Bus Stop and easily
              accessible from Avinashi Road and the surrounding Coimbatore neighbourhoods.
            </p>
          </div>

          <div className="contact-form-card">
            <h2>Send an Enquiry</h2>
            <form onSubmit={handleSubmit} noValidate>
              <div className="contact-form-card__row">
                <div className="contact-form-card__field">
                  <label htmlFor="c-name">Full Name</label>
                  <input id="c-name" type="text" value={form.fullName} onChange={handleChange("fullName")} aria-invalid={!!errors.fullName} />
                  {errors.fullName && <span className="contact-form-card__error">{errors.fullName}</span>}
                </div>
                <div className="contact-form-card__field">
                  <label htmlFor="c-phone">Phone Number</label>
                  <input id="c-phone" type="tel" value={form.phone} onChange={handleChange("phone")} aria-invalid={!!errors.phone} />
                  {errors.phone && <span className="contact-form-card__error">{errors.phone}</span>}
                </div>
              </div>

              <div className="contact-form-card__field">
                <label htmlFor="c-email">Email Address</label>
                <input id="c-email" type="email" value={form.email} onChange={handleChange("email")} aria-invalid={!!errors.email} />
                {errors.email && <span className="contact-form-card__error">{errors.email}</span>}
              </div>

              <div className="contact-form-card__row">
                <div className="contact-form-card__field">
                  <label htmlFor="c-goal">Fitness Goal</label>
                  <select id="c-goal" value={form.goal} onChange={handleChange("goal")} aria-invalid={!!errors.goal}>
                    <option value="">Select a goal</option>
                    <option>Build Strength</option>
                    <option>Lose Weight</option>
                    <option>Build Muscle</option>
                    <option>General Fitness</option>
                  </select>
                  {errors.goal && <span className="contact-form-card__error">{errors.goal}</span>}
                </div>
                <div className="contact-form-card__field">
                  <label htmlFor="c-program">Preferred Program</label>
                  <select id="c-program" value={form.program} onChange={handleChange("program")} aria-invalid={!!errors.program}>
                    <option value="">Select a program</option>
                    <option>Strength Training</option>
                    <option>Functional Training</option>
                    <option>Weight Management</option>
                    <option>Personal Training</option>
                    <option>Cardio Conditioning</option>
                    <option>Muscle Building</option>
                  </select>
                  {errors.program && <span className="contact-form-card__error">{errors.program}</span>}
                </div>
              </div>

              <div className="contact-form-card__field">
                <label htmlFor="c-time">Preferred Contact Time</label>
                <select id="c-time" value={form.contactTime} onChange={handleChange("contactTime")}>
                  <option value="">Select a time</option>
                  <option>Morning</option>
                  <option>Afternoon</option>
                  <option>Evening</option>
                </select>
              </div>

              <div className="contact-form-card__field">
                <label htmlFor="c-message">Message</label>
                <textarea id="c-message" rows="4" value={form.message} onChange={handleChange("message")} />
              </div>

              <label className="contact-form-card__consent">
                <input type="checkbox" checked={form.consent} onChange={handleChange("consent")} aria-invalid={!!errors.consent} />
                <span>I consent to Firm Fit Factory contacting me about my enquiry.</span>
              </label>
              {errors.consent && <span className="contact-form-card__error">{errors.consent}</span>}

              <button type="submit" className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: "8px" }}>
                Send Enquiry
              </button>
              {submitted && <p className="contact-form-card__success" role="status">Thanks — your enquiry has been received. We'll be in touch soon.</p>}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
